'use client';

import { usePackagesParams } from '@hooks/use-packages-params';
import useStorage from '@hooks/useStorage';
import { useBuildPackagesStore } from '@store/build-package-store';
import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { useTicketsStore } from '@store/tickets-store';
import { trackGa4PackageComponentRemoved } from '@v2/analytics/ga4';
import { skipPackageComponent } from '@utils/skip-package-component';
import { v2Links } from '@utils/navigation/links';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  Minus,
  Plus,
  Users01,
} from '@v2/ui';
import { cn } from '@v2/utils';
import { useRouter } from 'next/navigation';
import React from 'react';

const ticketQuantityOptions = [1, 2, 3, 4, 5, 6, 7] as const;
const customQuantityMinimum = 8;

const ticketQuantityTitleClassName =
  'text-[28px] font-extrabold leading-9 tracking-[-0.7px] text-gray-950';
const ticketQuantityDescriptionClassName =
  'mt-3 flex flex-col items-center justify-center gap-1.5 text-base font-medium leading-6 text-gray-700';
const ticketQuantityContinueButtonClassName =
  'min-h-12 w-full rounded-xl text-base font-semibold';
const ticketQuantitySkipButtonClassName =
  'mt-4 self-center rounded-md px-3 py-1.5 text-base font-semibold text-gray-900 transition-colors hover:text-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

function updateTicketQuantity(quantity: number) {
  useTicketsStore.setState({
    ticketsQuantity: quantity,
    ticketSelected: null,
    isTicketSelectionConfirmed: false,
    taxQuota: {
      tax: 0,
      tax_signature: null,
    },
  });
}

function useTicketSkipAction() {
  const storage = useStorage();
  const router = useRouter();
  const { packageType, eventId, eventLink } = usePackagesParams();
  const canSkipTickets = packageType !== 'tickets';

  const handleSkip = React.useCallback(() => {
    if (!canSkipTickets) return;

    useCheckoutStepsStore.setState({ ticketSelected: false });
    useBuildPackagesStore.setState({ selectedTicketPrice: 0 });
    if (skipPackageComponent(storage, 'tickets')) {
      trackGa4PackageComponentRemoved({
        component: 'tickets',
        params: {
          event_id: eventId,
          package_type: 'package',
        },
      });
    }

    const nextPackageType = packageType || 'packages';
    router.push(
      `${v2Links.searchHotel}?eventId=${eventId}&eventLink=${eventLink}&packageType=${nextPackageType}`
    );
  }, [canSkipTickets, eventId, eventLink, packageType, router, storage]);

  return { canSkipTickets, handleSkip };
}

function SeatsTogetherHint() {
  return (
    <>
      <Users01 size="26" className="text-gray-900" />
      <span>Seats will be together</span>
    </>
  );
}

function TicketQuantitySkipButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Skip tickets"
      className={ticketQuantitySkipButtonClassName}
      onClick={onClick}
    >
      Skip
    </button>
  );
}

export function TicketQuantityStepper({
  value,
  onChange,
}: {
  value?: number;
  onChange?: (quantity: number) => void;
} = {}) {
  const storeTicketsQuantity = useTicketsStore(
    (state) => state.ticketsQuantity
  );
  const ticketsQuantity = value ?? storeTicketsQuantity;

  const handleDecrease = () => {
    const next = Math.max(1, ticketsQuantity - 1);
    if (onChange) {
      onChange(next);
    } else {
      updateTicketQuantity(next);
    }
  };

  const handleIncrease = () => {
    const next = ticketsQuantity + 1;
    if (onChange) {
      onChange(next);
    } else {
      updateTicketQuantity(next);
    }
  };

  return (
    <div className="flex items-center justify-center gap-x-3">
      <Button
        type="button"
        size="sm"
        variant="secondary"
        aria-label="Decrease ticket quantity"
        disabled={ticketsQuantity <= 1}
        onClick={handleDecrease}
        className="size-10 rounded-xl p-0"
      >
        <Minus size="20" />
      </Button>
      <span
        className="min-w-12 text-center text-3xl font-bold text-gray-800"
        aria-live="polite"
      >
        {ticketsQuantity}
      </span>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        aria-label="Increase ticket quantity"
        onClick={handleIncrease}
        className="size-10 rounded-xl p-0"
      >
        <Plus size="20" />
      </Button>
    </div>
  );
}

function useTicketQuantityPicker(defaultOpen: boolean) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const ticketsQuantity = useTicketsStore((state) => state.ticketsQuantity);
  const [draftQuantity, setDraftQuantity] = React.useState(ticketsQuantity);
  const { canSkipTickets, handleSkip } = useTicketSkipAction();

  React.useEffect(() => {
    if (defaultOpen) setIsOpen(true);
  }, [defaultOpen]);

  React.useEffect(() => {
    setDraftQuantity(ticketsQuantity);
  }, [ticketsQuantity]);

  const commitAndClose = () => {
    if (draftQuantity !== ticketsQuantity) {
      updateTicketQuantity(draftQuantity);
    }
    setIsOpen(false);
  };

  const skipAndClose = () => {
    setDraftQuantity(ticketsQuantity);
    setIsOpen(false);
    handleSkip();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    if (!nextOpen) {
      setDraftQuantity(ticketsQuantity);
    }
  };

  return {
    isOpen,
    ticketsQuantity,
    draftQuantity,
    setDraftQuantity,
    canSkipTickets,
    commitAndClose,
    skipAndClose,
    handleOpenChange,
  };
}

function TicketQuantityOptions({
  ticketsQuantity,
  onChange,
}: {
  ticketsQuantity: number;
  onChange: (quantity: number) => void;
}) {
  const isCustomOpen = ticketsQuantity >= customQuantityMinimum;
  const [customQuantity, setCustomQuantity] = React.useState(() =>
    String(Math.max(ticketsQuantity, customQuantityMinimum))
  );

  React.useEffect(() => {
    if (ticketsQuantity >= customQuantityMinimum) {
      setCustomQuantity(String(ticketsQuantity));
    }
  }, [ticketsQuantity]);

  const handleCustomOpen = () => {
    const nextQuantity = Math.max(ticketsQuantity, customQuantityMinimum);

    setCustomQuantity(String(nextQuantity));
    if (ticketsQuantity !== nextQuantity) onChange(nextQuantity);
  };

  const handleCustomQuantityChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.currentTarget.value;
    setCustomQuantity(nextValue);

    const nextQuantity = Number(nextValue);
    if (
      Number.isInteger(nextQuantity) &&
      nextQuantity >= customQuantityMinimum
    ) {
      onChange(nextQuantity);
    }
  };

  const handleCustomQuantityBlur = () => {
    const parsed = Number(customQuantity);
    if (!Number.isInteger(parsed) || parsed < customQuantityMinimum) {
      const fallback = Math.max(ticketsQuantity, customQuantityMinimum);
      setCustomQuantity(String(fallback));
      onChange(fallback);
    } else {
      onChange(parsed);
    }
  };

  return (
    <div className="space-y-2.5" role="group" aria-label="Ticket quantity">
      <div className="grid grid-cols-4 gap-3">
        {ticketQuantityOptions.map((quantity) => {
          const isSelected = ticketsQuantity === quantity;

          return (
            <button
              key={quantity}
              type="button"
              aria-label={`${quantity} tickets`}
              aria-pressed={isSelected}
              onClick={() => onChange(quantity)}
              className={cn(
                'flex min-h-[68px] items-center justify-center rounded-xl border-2 bg-white text-[21px] font-semibold tracking-[-0.4px] text-gray-950 transition-[border-color,background-color,box-shadow,transform,color] duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                'active:scale-[0.98]',
                isSelected
                  ? 'border-primary-500 bg-primary-50/30 text-primary-500 shadow-primaryShadow'
                  : 'border-gray-200 hover:border-gray-800 hover:bg-gray-50'
              )}
            >
              {quantity}
            </button>
          );
        })}

        <button
          type="button"
          aria-label="Choose 8 or more tickets"
          aria-pressed={ticketsQuantity >= customQuantityMinimum}
          onClick={handleCustomOpen}
          className={cn(
            'flex min-h-[68px] items-center justify-center rounded-xl border-2 bg-white text-[21px] font-semibold tracking-[-0.4px] text-gray-950 transition-[border-color,background-color,box-shadow,transform,color] duration-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            'active:scale-[0.98]',
            ticketsQuantity >= customQuantityMinimum
              ? 'border-primary-500 bg-primary-50/30 text-primary-500 shadow-primaryShadow'
              : 'border-gray-200 hover:border-gray-800 hover:bg-gray-50'
          )}
        >
          8+
        </button>
      </div>

      {isCustomOpen && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
          <label className="block" htmlFor="custom-ticket-quantity">
            <span className="mb-2 block text-sm font-semibold text-gray-800">
              Custom number of tickets
            </span>
            <input
              id="custom-ticket-quantity"
              type="number"
              inputMode="numeric"
              min={customQuantityMinimum}
              step="1"
              value={customQuantity}
              onBlur={handleCustomQuantityBlur}
              onChange={handleCustomQuantityChange}
              className="h-12 w-full rounded-lg border border-gray-300 bg-white px-3 text-base font-semibold text-gray-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
            />
          </label>
          <p className="mt-1.5 text-xs font-medium text-gray-500">
            Enter 8 or more tickets.
          </p>
        </div>
      )}
    </div>
  );
}

export function TicketQuantityDrawer({
  compact = false,
  defaultOpen = true,
}: {
  compact?: boolean;
  defaultOpen?: boolean;
} = {}) {
  const {
    isOpen,
    ticketsQuantity,
    draftQuantity,
    setDraftQuantity,
    canSkipTickets,
    commitAndClose,
    skipAndClose,
    handleOpenChange,
  } = useTicketQuantityPicker(defaultOpen);

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        <Button
          size="xs"
          aria-label={`${ticketsQuantity} tickets`}
          className={cn(
            'bg-white',
            compact ? 'min-w-[118px] flex-1' : 'w-full'
          )}
          variant="secondary"
        >
          {ticketsQuantity}&nbsp;tickets
        </Button>
      </DrawerTrigger>

      <DrawerContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="mx-auto max-h-[min(88svh,38rem)] w-full max-w-[520px] overflow-y-auto rounded-t-[24px] border-0 bg-white px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-shadowLg sm:px-6 [&>div:first-child]:mt-3 [&>div:first-child]:h-1 [&>div:first-child]:w-10 [&>div:first-child]:bg-gray-300"
      >
        <DrawerHeader className="px-2 pb-5 pt-5 text-center">
          <DrawerTitle className={ticketQuantityTitleClassName}>
            How many tickets?
          </DrawerTitle>
          <DrawerDescription className={ticketQuantityDescriptionClassName}>
            <SeatsTogetherHint />
          </DrawerDescription>
        </DrawerHeader>

        <div className="space-y-4 px-2">
          <TicketQuantityOptions
            ticketsQuantity={draftQuantity}
            onChange={setDraftQuantity}
          />
        </div>

        <DrawerFooter className="mt-0 gap-0 px-2 pb-0 pt-5">
          <DrawerClose asChild>
            <Button
              variant="primary"
              className={ticketQuantityContinueButtonClassName}
              onClick={commitAndClose}
            >
              Continue
            </Button>
          </DrawerClose>
          {canSkipTickets && (
            <TicketQuantitySkipButton onClick={skipAndClose} />
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export function TicketQuantityDialog({
  defaultOpen = true,
}: {
  defaultOpen?: boolean;
} = {}) {
  const {
    isOpen,
    draftQuantity,
    setDraftQuantity,
    canSkipTickets,
    commitAndClose,
    skipAndClose,
    handleOpenChange,
  } = useTicketQuantityPicker(defaultOpen);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <div className="flex flex-col items-center justify-center gap-y-3 lg:gap-y-4">
        <DialogTrigger asChild>
          <button
            type="button"
            aria-label="Open ticket quantity popup"
            className="rounded-md px-2 text-2xl font-bold text-gray-800 transition-colors hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 lg:text-3xl"
          >
            How many tickets?
          </button>
        </DialogTrigger>
        <TicketQuantityStepper />
      </div>

      <DialogContent
        showCloseButton={false}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="mx-auto flex max-h-[min(88svh,38rem)] w-full max-w-[520px] flex-col gap-0 overflow-y-auto rounded-[24px] p-6"
      >
        <DialogHeader className="items-center px-2 pb-5 pt-5 text-center">
          <DialogTitle className={ticketQuantityTitleClassName}>
            How many tickets?
          </DialogTitle>
          <DialogDescription className={ticketQuantityDescriptionClassName}>
            <SeatsTogetherHint />
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-2">
          <TicketQuantityOptions
            ticketsQuantity={draftQuantity}
            onChange={setDraftQuantity}
          />
        </div>

        <div className="mt-0 flex flex-col gap-0 px-2 pb-0 pt-5">
          <DialogClose asChild>
            <Button
              variant="primary"
              className={ticketQuantityContinueButtonClassName}
              onClick={commitAndClose}
            >
              Continue
            </Button>
          </DialogClose>
          {canSkipTickets && (
            <TicketQuantitySkipButton onClick={skipAndClose} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
